import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useSiteContent } from '../../contexts/SiteContentContext';
import { NextCta } from '../Layout';
import { FadeLink, Section, cx, useCopy, useLightbox, useParallax } from '../core';
import { useProductStatus } from '../ProductCards';
import { PRODUCT_DETAILS, detailCopy } from '../productContent';
import NotFoundPage from './NotFound';

const pick = (value, language) => (value && typeof value === 'object' ? value[language] || value.en : value);
const isExternal = (url) => /^https?:/.test(url);

// Links to the separately deployed product apps are plain anchors, not router links.
function ProductLink({ link, className, children }) {
  return <a className={className} href={link.url} {...(isExternal(link.url) ? { target: '_blank', rel: 'noopener noreferrer' } : {})}>{children}</a>;
}

function Tile({ tile }) {
  if (tile.image) return <img className="itile" src={tile.image} alt="" />;
  return <span className="itile itile-letter" style={{ background: tile.color }} aria-hidden="true">{tile.letter}</span>;
}

function Hero({ product, slug, copy, text, links, language, status }) {
  const [go, setGo] = useState(false);
  useEffect(() => { const frame = requestAnimationFrame(() => setGo(true)); return () => cancelAnimationFrame(frame); }, []);
  const primary = links[0];
  const contactTo = `/contact?type=product&product=${slug}`;
  return <section className="lhero" aria-labelledby="h1">
    <div className="grid" aria-hidden="true" />
    <div className="glow" aria-hidden="true" />
    <div className="wrap"><FadeLink className="back" to="/products"><span aria-hidden="true">←</span> {copy.back}</FadeLink></div>
    <div className="wrap copy hin">
      <span className="apill" style={{ '--i': 0 }}><i />{status}</span>
      <h1 className="lh1" id="h1" style={{ '--i': 1 }}>
        <span className="l1"><span>{product.name}</span><Tile tile={product.tile} /><span>{text.lead}</span></span>
        {text.rest && <><br /> {text.rest}</>}
      </h1>
      <p className="intro" style={{ '--i': 2 }}>{text.intro}</p>
      <div className="ctas" style={{ '--i': 3 }}>
        {primary
          ? <><ProductLink className="btn btn-dark" link={primary}>{pick(primary.label, language)} <span className="ar" aria-hidden="true">↗</span></ProductLink>
            <FadeLink className="btn btn-wl" to={contactTo}>{copy.talk}</FadeLink></>
          : <><FadeLink className="btn btn-dark" to={contactTo}>{copy.talk} <span className="ar" aria-hidden="true">↗</span></FadeLink>
            <a className="btn btn-wl" href="#capabilities">{copy.seeCapabilities}</a></>}
      </div>
    </div>
    <div className={cx('fan', go && 'go', product.fan.length === 1 && 'single')} role="group" aria-label={copy.screenshots(product.name)}>
      <div className="wrapc" data-px=".05">
        {product.fan.map((card) => <div key={card.className} className={cx('card', card.className)}><img src={card.src} alt={pick(card.alt, language)} /></div>)}
      </div>
    </div>
    <div className="bfade" aria-hidden="true" />
  </section>;
}

function Capabilities({ product, copy, text, links, language }) {
  const primary = links[0];
  const rows = [];
  for (let i = 0; i < product.features.length; i += 3) rows.push(product.features.slice(i, i + 3));
  return <Section className="caps" id="capabilities">
    <div className="wrap">
      <div className="shead">
        <div data-reveal="" style={{ '--i': 0 }}><p className="lbl">{copy.capsLabel}</p><h2>{copy.capsTitle}</h2></div>
        <div className="r" data-reveal="" style={{ '--i': 1 }}><p>{text.capsText}</p>
          {primary
            ? <ProductLink className="link" link={primary}>{pick(primary.label, language)} <span className="ar" aria-hidden="true">↗</span></ProductLink>
            : <a className="link" href="#explore">{copy.getStarted} <span className="ar" aria-hidden="true">↗</span></a>}
        </div>
      </div>
      {product.frame && <div className="kframe" data-reveal="" style={{ '--i': 2 }}><img src={product.frame.src} alt={pick(product.frame.alt, language)} loading="lazy" /></div>}
      <div className={cx('tiles', !product.frame && 'plain')}>
        {rows.map((row, rowIndex) => <div key={rowIndex} className="trow">
          {row.map((feature, index) => {
            const [title, description] = feature[language] || feature.en;
            const number = rowIndex * 3 + index;
            return <div key={title} className="tile" data-reveal="" style={{ '--i': number }}>
              {feature.image ? <img className="vis" src={feature.image} alt="" loading="lazy" /> : <span className="num">{`0${number + 1}`}</span>}
              <h3>{title}</h3><p>{description}</p>
            </div>;
          })}
        </div>)}
      </div>
      <p className="note" data-reveal="">{text.note}</p>
    </div>
  </Section>;
}

function Roles({ product, copy, text, language, openLightbox }) {
  const [selected, setSelected] = useState(0);
  const tabs = useRef([]);
  const roles = product.roles;
  const select = (index, focus) => { setSelected(index); if (focus) tabs.current[index]?.focus(); };
  const current = roles[selected][language] || roles[selected].en;
  return <Section className="ways" id="roles">
    <div className="wrap">
      <div className="shead">
        <div data-reveal="" style={{ '--i': 0 }}><p className="lbl">{copy.waysLabel}</p><h2>{text.waysTitle}</h2></div>
        <div className="r" data-reveal="" style={{ '--i': 1 }}><p>{text.waysText}</p></div>
      </div>
      <div className="rtabs" role="tablist" aria-label={copy.roles} data-reveal="" style={{ '--i': 2 }}>
        {roles.map((role, index) => {
          const [label, description] = role[language] || role.en;
          return <button key={role.id} ref={(node) => { tabs.current[index] = node; }} className="rtab" role="tab" type="button" id={`tab-${role.id}`} aria-controls="rp"
            aria-selected={index === selected} tabIndex={index === selected ? 0 : -1} onClick={() => select(index)}
            onKeyDown={(event) => {
              if (event.key === 'ArrowRight' || event.key === 'ArrowDown') { event.preventDefault(); select((index + 1) % roles.length, true); }
              if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') { event.preventDefault(); select((index + roles.length - 1) % roles.length, true); }
            }}>
            <span className="t">{label} <span aria-hidden="true">→</span></span><span className="d">{description}</span>
          </button>;
        })}
      </div>
      <div className="rpanel" id="rp" role="tabpanel" aria-labelledby={`tab-${roles[selected].id}`} data-reveal="" style={{ '--i': 3 }}>
        <span className="chip">{current[1]}</span>
        <span className="src">{current[2]}</span>
        {roles.map((role, index) => <div key={role.id} className={cx('view', index === selected && 'on')}>
          <img className={role.view} src={role.src} alt={(role[language] || role.en)[4]} loading={index ? 'lazy' : undefined} />
        </div>)}
        <div className="shade" aria-hidden="true" />
        <div className="cap"><p>{current[3]}</p><button type="button" className="enl" onClick={() => openLightbox(roles[selected].src, current[4])}>{copy.enlarge} <span aria-hidden="true">↗</span></button></div>
      </div>
      <p className="note" data-reveal="">{text.waysNote}</p>
    </div>
  </Section>;
}

function Explore({ product, slug, copy, text, links, language }) {
  return <Section className="explore" id="explore">
    <div className="wrap">
      <div data-reveal="" style={{ '--i': 0 }}>
        <a className="link" href="#explore">{copy.getStarted} <span className="ar" aria-hidden="true">↗</span></a>
        <h2>{copy.explore(product.name)}</h2>
        <p className="sub">{copy.exploreSub}</p>
      </div>
      <div className="dest" data-reveal="" style={{ '--i': 1 }}>
        {links.map((link, index) => <ProductLink key={link.url} className={index === 0 ? 'pri' : undefined} link={link}>
          <span><b>{pick(link.label, language)}</b><small>{link.platform ? `${link.platform} · ${copy.official}` : copy.official}</small></span><i aria-hidden="true">↗</i>
        </ProductLink>)}
        <FadeLink className={links.length ? undefined : 'pri'} to={`/contact?type=product&product=${slug}`}><span><b>{copy.enquiry}</b><small>{text.onboarding}</small></span><i aria-hidden="true">↗</i></FadeLink>
      </div>
    </div>
  </Section>;
}

function Detail({ slug }) {
  const { t: copy, language } = useCopy(detailCopy);
  const { products } = useSiteContent();
  const status = useProductStatus();
  const lightbox = useLightbox();
  const product = PRODUCT_DETAILS[slug];
  const text = product[language] || product.en;
  const links = products.find((item) => item.slug === slug)?.links || [];
  useParallax([slug]);
  const props = { product, slug, copy, text, links, language };
  const primary = links[0];
  return <>
    <Hero {...props} status={status(slug)} />
    <Capabilities {...props} />
    {product.roles && <Roles {...props} openLightbox={lightbox.open} />}
    <Explore {...props} />
    <NextCta secondary={primary && <ProductLink className="btn btn-ghost" link={primary}>{pick(primary.label, language)} <span className="ar" aria-hidden="true">↗</span></ProductLink>} />
    {lightbox.element}
  </>;
}

export default function ProductDetailPage() {
  const { slug } = useParams();
  if (!PRODUCT_DETAILS[slug]) return <NotFoundPage />;
  return <Detail key={slug} slug={slug} />;
}
